import { useState } from "react";
import { InfoTip } from "./InfoTip";
import { downloadCSV } from "../utils/csv";
import type { LarScenario, LarDropLevel, LiquidityData, ProtocolFilter, CascadeResult } from "../types";

interface LiquiditySectionProps {
  lar: Record<string, LarScenario>;
  liquidity?: LiquidityData;
  protocolFilter: ProtocolFilter;
  cascade?: Record<string, CascadeResult>;
}

type ScenarioId = "eth_crash" | "btc_crash" | "broad_crash" | "lst_depeg" | "ethena_depeg";

const SCENARIO_TABS: { id: ScenarioId; short: string }[] = [
  { id: "lst_depeg", short: "LST Depeg" },
  { id: "ethena_depeg", short: "Ethena Depeg" },
  { id: "eth_crash", short: "ETH Crash" },
  { id: "btc_crash", short: "BTC Crash" },
  { id: "broad_crash", short: "Broad Market" },
];

const MODE_LABELS: Record<string, { label: string; short: string; tip: string }> = {
  static: { label: "Conservative", short: "Cons.", tip: "No liquidity recovery between liquidation waves. Best estimate for rapid cascades executing within minutes." },
  partial: { label: "Moderate", short: "Mod.", tip: "30% depth recovery between waves. Some arbitrageur refills between blocks." },
  full: { label: "Optimistic", short: "Opt.", tip: "Full depth recovery between waves. Assumes well-capitalized liquidators and time for order books to refill." },
};

export function LiquiditySection({ lar, liquidity, cascade }: LiquiditySectionProps) {
  const [activeScenario, setActiveScenario] = useState<ScenarioId>("lst_depeg");
  const scenario = lar[activeScenario];
  if (!scenario) return null;

  const drops = scenario.lar_by_drop;
  const scanAge = liquidity?.scan_age_seconds;
  const stale = scanAge != null && scanAge > 14400;

  // Find cascade drop levels available for this scenario
  const cascadeDrops = cascade
    ? [...new Set(
        Object.values(cascade)
          .filter((v) => v.scenario_id === activeScenario)
          .map((v) => v.initial_drop_pct)
      )].sort((a, b) => a - b)
    : [];
  const [cascadeDropPct, setCascadeDropPct] = useState<number>(5);
  // Auto-select a valid drop level when scenario changes
  const effectiveDrop = cascadeDrops.includes(cascadeDropPct)
    ? cascadeDropPct
    : cascadeDrops[Math.min(3, cascadeDrops.length - 1)] || cascadeDropPct;

  // Filter cascade results for active scenario + drop level
  const cascadeForScenario = cascade
    ? Object.entries(cascade).filter(([k]) =>
        k.startsWith(activeScenario + "_" + effectiveDrop + "pct_"))
    : [];
  const hasCascade = cascadeForScenario.length > 0;
  const cascadeByMode: Record<string, CascadeResult> = {};
  for (const [, result] of cascadeForScenario) {
    cascadeByMode[result.replenishment] = result;
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold">
            <InfoTip text="Liquidity-at-Risk measures whether DEX markets can absorb the liquidations that CaR predicts. Includes cascade simulation: selling pressure from initial liquidations moves prices, triggering further rounds of liquidation.">
              Liquidity-at-Risk (LaR)
            </InfoTip>
          </h2>
          <p className="text-xs text-text-muted mt-0.5">
            Can DEX liquidity absorb the liquidations?
            {scanAge != null && (
              stale ? (
                <span className="ml-2 px-2 py-0.5 rounded bg-risk-warning/10 text-risk-warning text-[10px] font-medium">
                  ⚠ Depth data: {formatAge(scanAge)} — liquidity estimates may be inaccurate
                </span>
              ) : (
                <span className="text-text-muted ml-2">
                  Depth data: {formatAge(scanAge)}
                </span>
              )
            )}
          </p>
        </div>
        <div className="flex items-center gap-1 bg-surface-700 rounded-lg p-0.5 overflow-x-auto">
          {SCENARIO_TABS.map(({ id, short }) => {
            const hasData = lar[id] != null;
            return (
              <button
                key={id}
                onClick={() => hasData && setActiveScenario(id)}
                disabled={!hasData}
                className={`
                  px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap
                  ${activeScenario === id
                    ? "bg-accent text-white"
                    : hasData
                      ? "text-text-secondary hover:text-text-primary hover:bg-surface-600"
                      : "text-text-muted opacity-40 cursor-not-allowed"
                  }
                `}
              >
                {short}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cascade summary — always shown when available */}
      {hasCascade && (
        <CascadeSummary
          byMode={cascadeByMode}
          drops={cascadeDrops}
          activeDrop={effectiveDrop}
          onDropChange={setCascadeDropPct}
        />
      )}

      {/* First-order LaR shortfall cards */}
      <div className="mb-1">
        {hasCascade && (
          <p className="text-[10px] text-text-muted uppercase tracking-wider mb-2 font-semibold">
            <InfoTip text="First-order shortfall shows the immediate liquidation demand before cascade effects. The cascade section above shows the full picture including second-order effects.">
              First-Order Shortfall by Drop Level
            </InfoTip>
          </p>
        )}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-8 gap-2 mb-4">
          {drops.map((level) => (
            <div key={level.drop_pct} className="card-compact text-center">
              <p className="metric-label">-{level.drop_pct}%</p>
              {(() => {
                const shortfall = level.assets.reduce((s, a) => s + a.lar_usd, 0);
                return (
                  <>
                    <p className={`text-sm font-mono font-semibold mt-1 ${shortfall > 0 ? "text-risk-danger" : "text-risk-safe"}`}>
                      {shortfall > 0 ? formatCompact(shortfall) : "$0"}
                    </p>
                    <p className="text-xs text-text-muted font-mono">
                      {(() => {
                        const covered = level.assets.reduce((s, a) => s + Math.min(a.demand_usd, a.available_usd), 0);
                        const demand = level.assets.reduce((s, a) => s + a.demand_usd, 0);
                        return demand > 0 ? `${(covered / demand * 100).toFixed(0)}% absorbed` : "";
                      })()}
                    </p>
                  </>
                );
              })()}
            </div>
          ))}
        </div>
      </div>

      {/* Detailed table */}
      <DropDetail drops={drops} />

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs text-text-muted">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-safe" /> Sufficient liquidity</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-danger" /> Shortfall</span>
        <span className="flex items-center gap-1"><span className="text-risk-warning">⚠</span> No DEX depth data</span>
        <span className="flex items-center gap-1">* Default bonus (not on-chain)</span>
      </div>
    </div>
  );
}

// ── Cascade Summary ────────────────────────────────────────────────

function CascadeSummary({ byMode, drops, activeDrop, onDropChange }: {
  byMode: Record<string, CascadeResult>;
  drops: number[];
  activeDrop: number;
  onDropChange: (d: number) => void;
}) {
  const [activeMode, setActiveMode] = useState<string>("static");
  const modes = ["static", "partial", "full"].filter((m) => byMode[m]);
  if (modes.length === 0) return null;

  const active = byMode[activeMode] || byMode[modes[0]];
  const firstOrder = active.first_order_car_usd;
  const cascadeExtra = active.total_car_usd - firstOrder;

  return (
    <div className="mb-4 p-3 rounded-lg bg-surface-800/50 border border-surface-600">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-text-primary">
          <InfoTip text="Cascade simulation models second-order effects: initial liquidations cause selling pressure, which moves prices, triggering additional liquidations. Price impact is derived from real DEX slippage curves.">
            Cascade Simulation
          </InfoTip>
        </h3>
        <div className="flex items-center gap-2">
          {/* Drop level selector */}
          <div className="flex items-center gap-1 bg-surface-700 rounded-md p-0.5">
            {drops.map((d) => (
              <button
                key={d}
                onClick={() => onDropChange(d)}
                className={`px-2 py-1 text-[10px] font-mono font-medium rounded transition-colors ${
                  activeDrop === d
                    ? "bg-accent text-white"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                {d}%
              </button>
            ))}
          </div>
          {/* Mode selector */}
          <div className="flex items-center gap-1 bg-surface-700 rounded-md p-0.5">
            {modes.map((mode) => {
              const meta = MODE_LABELS[mode];
              return (
                <button
                  key={mode}
                  onClick={() => setActiveMode(mode)}
                  className={`px-2.5 py-1 text-[10px] font-medium rounded transition-colors ${
                    activeMode === mode
                      ? "bg-accent text-white"
                      : "text-text-secondary hover:text-text-primary"
                  }`}
                  title={meta.tip}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top-line metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div className="p-2 rounded-lg bg-surface-800">
          <p className="text-[10px] text-text-muted tracking-wider">
            <InfoTip position="below" text="Liquidation volume from the initial price shock alone, before cascade effects.">
              1st-Order CaR
            </InfoTip>
          </p>
          <p className="text-sm font-mono font-semibold text-text-primary">{formatCompact(firstOrder)}</p>
        </div>
        <div className="p-2 rounded-lg bg-surface-800">
          <p className="text-[10px] text-text-muted tracking-wider">
            <InfoTip position="below" text="Total liquidation volume after all cascade rounds complete. Includes first-order plus all subsequent waves.">
              Total CaR (all rounds)
            </InfoTip>
          </p>
          <p className="text-sm font-mono font-semibold text-text-primary">
            {formatCompact(active.total_car_usd)}
            <span className="text-[10px] text-text-muted ml-1">({active.cascade_multiplier.toFixed(2)}×)</span>
          </p>
        </div>
        <div className="p-2 rounded-lg bg-surface-800">
          <p className="text-[10px] text-text-muted tracking-wider">
            <InfoTip position="below" text="Liquidation volume that DEX markets can absorb across all rounds.">
              Absorbed by DEX
            </InfoTip>
          </p>
          <p className="text-sm font-mono font-semibold text-risk-safe">{formatCompact(active.total_executed_usd)}</p>
        </div>
        <div className="p-2 rounded-lg bg-surface-800">
          <p className="text-[10px] text-text-muted tracking-wider">
            <InfoTip position="below" text="Total liquidation volume that cannot be absorbed — becomes protocol losses (bad debt) distributed across depositors or the safety module.">
              Potential Bad Debt
            </InfoTip>
          </p>
          <p className="text-sm font-mono font-semibold text-risk-danger">{formatCompact(active.total_bad_debt_usd)}</p>
        </div>
      </div>

      {/* Plain-English summary */}
      <div className="text-xs text-text-secondary leading-relaxed bg-surface-900/50 rounded-md px-3 py-2 mb-3">
        <p>
          A {active.initial_drop_pct}% shock triggers{" "}
          <span className="font-mono font-semibold text-text-primary">{formatCompact(firstOrder)}</span>{" "}
          of first-order liquidations.
          {cascadeExtra > 100_000 ? (
            <>{" "}Selling pressure pushes prices down further, triggering an additional{" "}
            <span className="font-mono font-semibold text-text-primary">{formatCompact(cascadeExtra)}</span>{" "}
            across {active.num_rounds - 1} subsequent wave{active.num_rounds > 2 ? "s" : ""} ({active.cascade_multiplier.toFixed(2)}× total).{" "}
            Of {formatCompact(active.total_car_usd)} total CaR,{" "}
            <span className="font-mono font-semibold text-risk-danger">{formatCompact(active.total_bad_debt_usd)}</span>{" "}
            cannot be absorbed by DEX markets.</>
          ) : (
            <>{" "}Cascade effects are minimal — selling pressure does not significantly move prices.</>
          )}
        </p>
      </div>

      {/* Round-by-round table */}
      {active.rounds.length > 1 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-text-muted">
                <th className="text-left py-1.5 pr-3 font-medium">Round</th>
                <th className="text-right py-1.5 px-2 font-medium">
                  <InfoTip position="below" text="New collateral entering liquidation this round, triggered by price impact from prior rounds.">New CaR</InfoTip>
                </th>
                <th className="text-right py-1.5 px-2 font-medium">
                  <InfoTip position="below" text="How many new positions broke HF=1 this round.">Positions</InfoTip>
                </th>
                <th className="text-right py-1.5 px-2 font-medium">
                  <InfoTip position="below" text="Portion absorbed by DEX liquidity this round.">Absorbed</InfoTip>
                </th>
                <th className="text-right py-1.5 px-2 font-medium">
                  <InfoTip position="below" text="Liquidation volume exceeding available DEX liquidity — becomes protocol loss.">Bad Debt</InfoTip>
                </th>
                <th className="text-right py-1.5 px-2 font-medium">
                  <InfoTip position="below" text="Additional price drop caused by this round's selling, feeding into the next round.">Price Impact</InfoTip>
                </th>
                <th className="text-right py-1.5 pl-2 font-medium">
                  <InfoTip position="below" text="Running total of all CaR across rounds 0 through this round.">Cumulative CaR</InfoTip>
                </th>
              </tr>
            </thead>
            <tbody>
              {active.rounds.map((r) => {
                // Show price impact for the asset with the largest CaR this round (not highest %)
                const assetsByVolume = Object.entries(r.per_asset).sort((a, b) => b[1].car - a[1].car);
                const topAsset = assetsByVolume[0]?.[0];
                const topImpact: [string, number] | undefined = topAsset && r.price_impacts[topAsset] != null
                  ? [topAsset, r.price_impacts[topAsset]]
                  : Object.entries(r.price_impacts).sort((a, b) => b[1] - a[1])[0];
                return (
                  <tr key={r.round} className="border-t border-surface-600">
                    <td className="py-1.5 pr-3 font-sans text-text-primary font-medium">
                      {r.round === 0 ? <strong>Initial shock</strong> : `Wave ${r.round}`}
                    </td>
                    <td className="text-right py-1.5 px-2">{formatCompact(r.new_car_usd)}</td>
                    <td className="text-right py-1.5 px-2 text-text-secondary">{r.new_positions.toLocaleString()}</td>
                    <td className="text-right py-1.5 px-2 text-risk-safe">{formatCompact(r.executed_usd)}</td>
                    <td className={`text-right py-1.5 px-2 ${r.bad_debt_usd > 0 ? "text-risk-danger" : "text-risk-safe"}`}>
                      {formatCompact(r.bad_debt_usd)}
                    </td>
                    <td className="text-right py-1.5 px-2 text-text-secondary">
                      {topImpact ? `${(topImpact[1] * 100).toFixed(2)}%` : "—"}
                      {topImpact && <span className="text-text-muted text-[10px] ml-1">{topImpact[0]}</span>}
                    </td>
                    <td className="text-right py-1.5 pl-2 font-semibold text-text-primary">{formatCompact(r.cumulative_car_usd)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Effective price drop table */}
      {Object.keys(active.cumulative_price_impacts || {}).length > 0 && (() => {
        const MIN_DROP = 0.01;
        const shockedSet = new Set(active.shocked_assets || []);
        const allAssets = Object.entries(active.cumulative_price_impacts)
          .map(([asset, cascadeImpact]) => {
            const isShocked = shockedSet.has(asset);
            const initialShock = isShocked ? active.initial_drop_pct / 100 : 0;
            const totalDrop = initialShock + cascadeImpact;
            return { asset, isShocked, cascadeImpact, totalDrop };
          })
          .sort((a, b) => b.totalDrop - a.totalDrop);
        const shown = allAssets.filter((a) => a.totalDrop >= MIN_DROP);
        const hidden = allAssets.length - shown.length;

        return shown.length > 0 ? (
          <div className="mt-3 mb-2">
            <h4 className="text-xs font-semibold text-text-primary mb-2">
              <InfoTip position="below" text="Total effective price drop per asset after cascade selling. Assets with less than 1% total impact are hidden.">
                Effective Price Drop by Asset
              </InfoTip>
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-text-muted">
                    <th className="text-left py-1.5 pr-3 font-medium">Asset</th>
                    <th className="text-right py-1.5 px-2 font-medium">Cascade Selling Impact</th>
                    <th className="text-right py-1.5 px-2 font-medium">Total Effective Drop</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(({ asset, isShocked, cascadeImpact, totalDrop }) => (
                    <tr key={asset} className="border-t border-surface-600">
                      <td className="py-1.5 pr-3 font-sans font-medium text-text-primary">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${isShocked ? "bg-risk-danger" : "bg-risk-warning"}`} />
                        {asset}
                      </td>
                      <td className="text-right py-1.5 px-2 text-text-secondary">
                        +{(cascadeImpact * 100).toFixed(2)}%
                      </td>
                      <td className={`text-right py-1.5 px-2 font-semibold ${
                        totalDrop > 0.10 ? "text-risk-danger" :
                        totalDrop > 0.05 ? "text-risk-warning" : "text-text-primary"
                      }`}>
                        {(totalDrop * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-text-muted">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-risk-danger" /> Directly shocked ({active.initial_drop_pct}% + cascade)</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-risk-warning" /> Cascade contagion only</span>
              {hidden > 0 && <span className="ml-auto">{hidden} asset{hidden > 1 ? "s" : ""} with &lt;1% impact not shown</span>}
            </div>
          </div>
        ) : null;
      })()}

      {/* Status line */}
      <div className="flex items-center justify-between mt-2 text-[10px] text-text-muted">
        <span>{active.initial_drop_pct}% initial shock · {MODE_LABELS[activeMode]?.label || activeMode} assumption</span>
        <div className="flex items-center gap-3">
          <span>{active.converged ? `Converged in ${active.num_rounds} rounds` : `⚠ Hit max rounds (${active.num_rounds})`}</span>
          {active.rounds.length > 0 && (
            <button
              onClick={() => downloadCSV(
                active.rounds.map((r) => {
                  // Show price impact for the asset with the largest CaR this round (not highest %)
                const assetsByVolume = Object.entries(r.per_asset).sort((a, b) => b[1].car - a[1].car);
                const topAsset = assetsByVolume[0]?.[0];
                const topImpact: [string, number] | undefined = topAsset && r.price_impacts[topAsset] != null
                  ? [topAsset, r.price_impacts[topAsset]]
                  : Object.entries(r.price_impacts).sort((a, b) => b[1] - a[1])[0];
                  return {
                    round: r.round === 0 ? "Initial shock" : `Wave ${r.round}`,
                    new_car_usd: r.new_car_usd,
                    positions: r.new_positions,
                    absorbed_usd: r.executed_usd,
                    bad_debt_usd: r.bad_debt_usd,
                    price_impact_pct: topImpact ? topImpact[1] : null,
                    price_impact_asset: topImpact ? topImpact[0] : null,
                    cumulative_car_usd: r.cumulative_car_usd,
                  };
                }),
                `cascade_${active.scenario_id}_${active.initial_drop_pct}pct_${activeMode}.csv`
              )}
              className="text-accent hover:text-accent/80 transition-colors"
            >
              ↓ CSV
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Drop Detail ────────────────────────────────────────────────────

function DropDetail({ drops }: { drops: LarDropLevel[] }) {
  const [selectedDrop, setSelectedDrop] = useState(1);
  const level = drops[Math.min(selectedDrop, drops.length - 1)];
  if (!level) return null;

  const assets = level.assets.filter((a) => a.car_usd > 0);

  return (
    <div>
      {/* Drop selector */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span className="text-xs text-text-muted mr-1">Price drop:</span>
        {drops.map((d, i) => (
          <button
            key={d.drop_pct}
            onClick={() => setSelectedDrop(i)}
            className={`px-2.5 py-1 text-xs font-mono font-medium rounded-md transition-colors ${
              i === selectedDrop
                ? "bg-accent text-white"
                : "bg-surface-700 text-text-secondary hover:text-text-primary hover:bg-surface-600"
            }`}
          >
            {d.drop_pct}%
          </button>
        ))}
      </div>

      {/* Summary row */}
      {(() => {
        const assetLarSum = level.assets.reduce((s, a) => s + a.lar_usd, 0);
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="p-2 rounded-lg bg-surface-800">
              <p className="text-[10px] text-text-muted tracking-wider">
                <InfoTip position="below" text="Total collateral that must be sold on DEXs under this scenario. This is the first-order liquidation demand before cascade effects.">
                  Liquidation Demand
                </InfoTip>
              </p>
              <p className="text-sm font-mono font-semibold text-text-primary">
                {formatCompact(level.assets.reduce((s, a) => s + a.demand_usd, 0))}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-surface-800">
              <p className="text-[10px] text-text-muted tracking-wider">
                <InfoTip position="below" text="Portion of first-order liquidation demand that DEX liquidity can absorb.">
                  Can Be Liquidated
                </InfoTip>
              </p>
              <p className="text-sm font-mono font-semibold text-risk-safe">
                {formatCompact(level.assets.reduce((s, a) => s + Math.min(a.demand_usd, a.available_usd), 0))}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-surface-800">
              <p className="text-[10px] text-text-muted tracking-wider">
                <InfoTip position="below" text="First-order shortfall only. See cascade section above for total shortfall including second-order effects.">
                  1st-Order Shortfall
                </InfoTip>
              </p>
              <p className={`text-sm font-mono font-semibold ${assetLarSum > 0 ? "text-risk-danger" : "text-risk-safe"}`}>
                {assetLarSum > 0 ? formatCompact(assetLarSum) : "$0"}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-surface-800">
              <p className="text-[10px] text-text-muted tracking-wider">
                <InfoTip position="below" text="What fraction of first-order demand the market can absorb.">
                  Absorbability
                </InfoTip>
              </p>
              {(() => {
                const covered = level.assets.reduce((s, a) => s + Math.min(a.demand_usd, a.available_usd), 0);
                const demand = level.assets.reduce((s, a) => s + a.demand_usd, 0);
                const ratio = demand > 0 ? covered / demand : 1;
                return (
                  <p className={`text-sm font-mono font-semibold ${
                    ratio >= 1 ? "text-risk-safe" :
                    ratio >= 0.5 ? "text-risk-warning" : "text-risk-danger"
                  }`}>
                    {demand > 0 ? `${(ratio * 100).toFixed(0)}%` : "—"}
                  </p>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* Per-asset table */}
      {assets.length > 0 && (
        <div className="flex justify-end mb-1">
          <button
            onClick={() => downloadCSV(
              assets.map((a) => ({
                asset: a.symbol,
                path: a.path,
                car_usd: a.car_usd,
                demand_usd: a.demand_usd,
                available_usd: a.has_depth_data ? a.available_usd : null,
                shortfall_usd: a.lar_usd,
                bonus_pct: a.bonus_pct,
                slippage_at_demand_pct: a.slippage_at_demand_pct,
              })),
              `lar_per_asset_${level.scenario}_${level.drop_pct}pct.csv`
            )}
            className="text-[10px] text-accent hover:text-accent/80 transition-colors"
          >
            ↓ CSV
          </button>
        </div>
      )}
      {assets.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-text-muted">
                <th className="text-left py-2 pr-3 font-medium">Asset</th>
                <th className="text-left py-2 pr-3 font-medium">Path</th>
                <th className="text-right py-2 px-2 font-medium">
                  <InfoTip position="below" text="Collateral value entering liquidation under this scenario.">CaR</InfoTip>
                </th>
                <th className="text-right py-2 px-2 font-medium">
                  <InfoTip position="below" text="Actual sell volume = CaR × 50% close factor.">Demand</InfoTip>
                </th>
                <th className="text-right py-2 px-2 font-medium">
                  <InfoTip position="below" text="Max trade size where slippage ≤ liquidation bonus.">Available</InfoTip>
                </th>
                <th className="text-right py-2 px-2 font-medium">
                  <InfoTip position="below" text="Shortfall = demand − available. Positive means the market can't absorb it.">Shortfall</InfoTip>
                </th>
                <th className="text-right py-2 px-2 font-medium">
                  <InfoTip position="below" text="Liquidation bonus threshold. For E-Mode positions this is typically 1-2%.">Bonus</InfoTip>
                </th>
                <th className="text-right py-2 px-2 font-medium">
                  <InfoTip position="below" text="Estimated slippage if the full demand were executed.">Slip@Demand</InfoTip>
                </th>
              </tr>
            </thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.symbol} className={`border-t border-surface-600 ${a.has_depth_data ? "" : "opacity-50"}`}>
                  <td className="py-2 pr-3 font-sans font-medium text-text-primary">
                    {a.symbol}
                    {!a.has_depth_data && (
                      <span className="ml-1 text-risk-warning text-[10px]" title="No DEX depth data">⚠</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-text-muted font-sans text-[11px]">{a.path}</td>
                  <td className="text-right py-2 px-2">{formatCompact(a.car_usd)}</td>
                  <td className="text-right py-2 px-2">{formatCompact(a.demand_usd)}</td>
                  <td className="text-right py-2 px-2 text-risk-safe">
                    {a.has_depth_data ? formatCompact(a.available_usd) : "—"}
                  </td>
                  <td className={`text-right py-2 px-2 font-semibold ${a.lar_usd > 0 ? "text-risk-danger" : "text-risk-safe"}`}>
                    {formatCompact(a.lar_usd)}
                  </td>
                  <td className="text-right py-2 px-2 text-text-secondary">
                    {(a.bonus_pct * 100).toFixed(1)}%
                    {a.bonus_hardcoded && <span className="text-risk-warning ml-0.5" title="Default value — not read from on-chain">*</span>}
                  </td>
                  <td className={`text-right py-2 px-2 ${slipColor(a.slippage_at_demand_pct, a.bonus_pct)}`}>
                    {a.slippage_at_demand_pct != null
                      ? `${(a.slippage_at_demand_pct * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function slipColor(slippage: number | null, bonus: number): string {
  if (slippage == null) return "text-text-muted";
  if (slippage > bonus) return "text-risk-danger";
  if (slippage > bonus * 0.5) return "text-risk-warning";
  return "text-risk-safe";
}

function formatCompact(value: number): string {
  if (value === 0) return "$0";
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${(seconds / 3600).toFixed(1)}h ago`;
}
